import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { toast } from 'react-toastify'
import { removeLoadingKey, removeOneLoadingKey } from '../helpers/common'

export const DemandPlanningLoadingKeys = {
    getSkuAllocationTableData: "GET_SKU_ALLOCATION_TABLE_DATA",
    getEligibilityValidationTableData: "GET_ELIGIBILITY_VALIDATION_TABLE_DATA",
    getHistoricalTableData: "GET_HISTORICAL_TABLE_DATA",
    getMonthlyTrendTableData: "GET_MONTHLY_TREND_TABLE_DATA",
    getForecastVsActualsTableData: "GET_FORECAST_VS_ACTUALS_TABLE_DATA",
    getForecastBySkuTableData: "GET_FORECAST_BY_SKU_TABLE_DATA",
    getOpenToBuyTableData: "GET_OPEN_TO_BUY_TABLE_DATA",
    getOverstockInventoryTableData: "GET_OVERSTOCK_INVENTORY_TABLE_DATA",
    getCashLiabilitiesTableData: "GET_CASH_LIABILITIES_TABLE_DATA",
    getUpcomingBuysTableData: "GET_UPCOMING_BUYS_TABLE_DATA",
    getPoScheduleTableData: "GET_PO_SCHEDULE_TABLE_DATA",
    getBundleDemandExplosionTableData: "GET_BUNDLE_DEMAND_EXPLOSION_TABLE_DATA",
    getBundleReplacementShiftTableData: "GET_BUNDLE_REPLACEMENT_SHIFT_TABLE_DATA",
    getReplacementDemandShiftTableData: "GET_REPLACEMENT_DEMAND_SHIFT_TABLE_DATA",
    getComponentDemandLedgerTableData: "GET_COMPONENT_DEMAND_LEDGER_TABLE_DATA",
    getPurchaseRoundingTableData: "GET_PURCHASE_ROUNDING_TABLE_DATA",
    getContainerOptimizationTableData: "GET_CONTAINER_OPTIMIZATION_TABLE_DATA",
    getInventoryCapitalPlanTableData: "GET_INVENTORY_CAPITAL_PLAN_TABLE_DATA",
    saveForecastDemandProductOverride: "SAVE_FORECAST_DEMAND_PRODUCT_OVERRIDE",
    resetForecastDemandProductOverrides: "RESET_FORECAST_DEMAND_PRODUCT_OVERRIDES",
    saveForecastDemandProductApproval: "SAVE_FORECAST_DEMAND_PRODUCT_APPROVAL",
    saveForecastDemandProductApprovedNotes: "SAVE_FORECAST_DEMAND_PRODUCT_APPROVED_NOTES",
    saveForecastDemandProductsDraftedData: "SAVE_FORECAST_DEMAND_PRODUCTS_DRAFTED_DATA",
    saveForecastDemandProductUpcomingBuyExclude: "SAVE_FORECAST_DEMAND_PRODUCT_UPCOMING_BUY_EXCLUDE",
    getApprovedBuysTableData: "GET_APPROVED_BUYS_TABLE_DATA",
    getForecastDemandPlanningData: "GET_FORECAST_DEMAND_PLANNING_DATA",
    refreshForecastDemandSalesSnapshot: "REFRESH_FORECAST_DEMAND_SALES_SNAPSHOT",
    fetchForecastDemandPlanningList: "FETCH_FORECAST_DEMAND_PLANNING_LIST",
    getDemandDistributionProfileData: "GET_DEMAND_DISTRIBUTION_PROFILE_DATA",
    createForecastDemandPlanning: "CREATE_FORECAST_DEMAND_PLANNING",
    updateForecastDemandPlanning: "UPDATE_FORECAST_DEMAND_PLANNING",
    deleteForecastDemandPlanning: "DELETE_FORECAST_DEMAND_PLANNING",
    getAiRebalanceHistory: "GET_AI_REBALANCE_HISTORY",
    createAiRebalanceHistory: "CREATE_AI_REBALANCE_HISTORY",
    getForecastDemandPlanningHistory: "GET_FORECAST_DEMAND_PLANNING_HISTORY",
    getSkuAllocationChangeLog: "GET_SKU_ALLOCATION_CHANGE_LOG",
    rollbackSkuAllocationChanges: "ROLLBACK_SKU_ALLOCATION_CHANGES",
    undoLastSkuAllocationBatch: "UNDO_LAST_SKU_ALLOCATION_BATCH",
    restoreSkuAllocationToTimestamp: "RESTORE_SKU_ALLOCATION_TO_TIMESTAMP",
}

const initialState = {
    loading: false,
    demandDistributionProfileData: null,
    skuAllocationTableData: [],
    eligibilityValidationTableData: [],
    monthlyTrendTableData: [],
    historicalTableData: [],
    forecastVsActualsTableData: [],
    cashLiabilitiesTableData: [],
    overstockInventoryTableData: [],
    forecastBySkuTableData: [],
    upcomingBuysTableData: null,
    poScheduleTableData: null,
    bundleDemandExplosionTableData: null,
    bundleReplacementShiftTableData: null,
    replacementDemandShiftTableData: null,
    componentDemandLedgerTableData: null,
    purchaseRoundingTableData: null,
    containerOptimizationTableData: null,
    inventoryCapitalPlanTableData: null,
    openToBuyTableData: [],
    approvedBuysTableData: [],
    forecastDemandPlanningData: null,
    forecastDemandPlanningList: [],
    forecastDemandPlanningListLoaded: false,
    forecastDemandPlanningListFetchFailed: false,
    aiRebalanceHistory: [],
    forecastDemandPlanningHistory: [],
    forecastDemandPlanningHistoryTotal: 0,
    forecastDemandPlanningHistoryPage: 1,
    forecastDemandPlanningHistoryPageSize: 25,
    skuAllocationChangeLog: [],
    skuAllocationChangeLogPagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 100,
        isTheLastPage: true,
    },
    loadingKeys: [],
    error: null,
    firstLoadDefaultTableComplete: false,
    forecastCatalogGetError: null,
}

const extractError = (error) => {
    if (typeof error.response?.data === 'string') {
        return error.response.data
    }
    return error.response?.data?.error || error.response?.data?.errors || error.message
}

export const getDemandDistributionProfileData = createAsyncThunk(
    'demandPlanning/getDemandDistributionProfileData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        categoryId,
        subcategoryId,
        status,
        searchTerm,
        startDate,
        endDate,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/demand-distribution-profile-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    categoryId,
                    subcategoryId,
                    status,
                    searchTerm,
                    startDate,
                    endDate,
                },
            })
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getForecastDemandPlanningData = createAsyncThunk(
    'demandPlanning/getForecastDemandPlanningData',
    async ({ id, forecastYear }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-demand-planning-data', {
                params: { id, forecastYear },
            })
            return data
        } catch (error) {
            console.log(error)
            // Axios GET cancel via global interceptor — must not clear Redux catalog on reject (see extraReducers).
            const isCanceled =
                axios.isCancel?.(error) ||
                error.code === 'ERR_CANCELED' ||
                error.message === 'canceled'
            if (isCanceled) {
                return rejectWithValue({ __abort: true })
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const refreshForecastDemandSalesSnapshot = createAsyncThunk(
    'demandPlanning/refreshForecastDemandSalesSnapshot',
    async ({ id, forecastYear }, { rejectWithValue }) => {
        try {
            const refreshTimeoutMs = process.env.FORECAST_REFRESH_TIMEOUT_MS
                ? parseInt(process.env.FORECAST_REFRESH_TIMEOUT_MS, 10)
                : 10 * 60 * 1000;
            const { data } = await axios.post(
                `/api/v1/forecast-demand-planning/${id}/refresh-sales-snapshot`,
                forecastYear != null && forecastYear !== '' ? { forecastYear } : {},
                { timeout: refreshTimeoutMs }
            )
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getOverstockInventoryTableData = createAsyncThunk(
    'demandPlanning/getOverstockInventoryTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/overstock-inventory-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const getOpenToBuyTableData = createAsyncThunk(
    'demandPlanning/getOpenToBuyTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/open-to-buy-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const getForecastBySkuTableData = createAsyncThunk(
    'demandPlanning/getForecastBySkuTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-by-sku-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const getForecastVsActualsTableData = createAsyncThunk(
    'demandPlanning/getForecastVsActualsTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-vs-actuals-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const getHistoricalTableData = createAsyncThunk(
    'demandPlanning/getHistoricalTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/historical-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const getMonthlyTrendTableData = createAsyncThunk(
    'demandPlanning/getMonthlyTrendTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/monthly-trend-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const getEligibilityValidationTableData = createAsyncThunk(
    'demandPlanning/getEligibilityValidationTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/eligibility-validation-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const getSkuAllocationTableData = createAsyncThunk(
    'demandPlanning/getSkuAllocationTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/sku-allocation-table-data', { params: {
                demandPlanningId,
                demandPlanningYear,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                searchTerm,
            }});
            return data;
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const saveForecastDemandProductOverride = createAsyncThunk(
    'demandPlanning/saveForecastDemandProductOverride',
    async (
        { demandPlanningId, demandPlanningYear, productId, field, value, changeSource, changeNote },
        { rejectWithValue }
    ) => {
        try {
            const payload = {
                demandPlanningId,
                demandPlanningYear,
                productId,
            }
            if (changeSource) payload.changeSource = changeSource
            if (changeNote) payload.changeNote = changeNote
            if (field === 'overridePercent') {
                payload.overridePercent = value == null ? null : value
            } else if (field === 'newProductOverride') {
                payload.newProductOverride = value == null ? null : value
            } else return rejectWithValue('Invalid field')
            const { data } = await axios.put('/api/v1/forecast-demand-product-override', payload)
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const resetForecastDemandProductOverrides = createAsyncThunk(
    'demandPlanning/resetForecastDemandProductOverrides',
    async (
        { demandPlanningId, demandPlanningYear, productIds },
        { rejectWithValue }
    ) => {
        try {
            const payload = {
                demandPlanningId,
                demandPlanningYear,
                productIds,
            }
            const { data } = await axios.put('/api/v1/forecast-demand-product-overrides-reset', payload)
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getSkuAllocationChangeLog = createAsyncThunk(
    'demandPlanning/getSkuAllocationChangeLog',
    async ({ demandPlanningId, demandPlanningYear, productId, page, limit }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/sku-allocation-change-log', { params: {
                demandPlanningId,
                demandPlanningYear,
                productId,
                page,
                limit,
            }});
            return data;
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const rollbackSkuAllocationChanges = createAsyncThunk(
    'demandPlanning/rollbackSkuAllocationChanges',
    async ({ changeLogId, batchId, note }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/sku-allocation-rollback', {
                changeLogId,
                batchId,
                note,
            })
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const undoLastSkuAllocationBatch = createAsyncThunk(
    'demandPlanning/undoLastSkuAllocationBatch',
    async ({ demandPlanningId, demandPlanningYear, note }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/sku-allocation-undo-last', {
                demandPlanningId,
                demandPlanningYear,
                note,
            })
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const restoreSkuAllocationToTimestamp = createAsyncThunk(
    'demandPlanning/restoreSkuAllocationToTimestamp',
    async ({ demandPlanningId, demandPlanningYear, restoreTo, note }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/sku-allocation-restore', {
                demandPlanningId,
                demandPlanningYear,
                restoreTo,
                note,
            })
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getUpcomingBuysTableData = createAsyncThunk(
    'demandPlanning/getUpcomingBuysTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        selectedBuyMonth,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/upcoming-buys-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                    selectedBuyMonth,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getPoScheduleTableData = createAsyncThunk(
    'demandPlanning/getPoScheduleTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/po-schedule-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getBundleDemandExplosionTableData = createAsyncThunk(
    'demandPlanning/getBundleDemandExplosionTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/bundle-demand-explosion-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getBundleReplacementShiftTableData = createAsyncThunk(
    'demandPlanning/getBundleReplacementShiftTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/bundle-replacement-shift-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getReplacementDemandShiftTableData = createAsyncThunk(
    'demandPlanning/getReplacementDemandShiftTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/replacement-demand-shift-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getComponentDemandLedgerTableData = createAsyncThunk(
    'demandPlanning/getComponentDemandLedgerTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/component-demand-ledger-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getPurchaseRoundingTableData = createAsyncThunk(
    'demandPlanning/getPurchaseRoundingTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/purchase-rounding-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getContainerOptimizationTableData = createAsyncThunk(
    'demandPlanning/getContainerOptimizationTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/container-optimization-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getInventoryCapitalPlanTableData = createAsyncThunk(
    'demandPlanning/getInventoryCapitalPlanTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/inventory-capital-plan-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getCashLiabilitiesTableData = createAsyncThunk(
    'demandPlanning/getCashLiabilitiesTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/cash-liabilities-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getApprovedBuysTableData = createAsyncThunk(
    'demandPlanning/getApprovedBuysTableData',
    async ({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
    }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/approved-buys-table-data', {
                params: {
                    demandPlanningId,
                    demandPlanningYear,
                    searchTerm,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    selectedStatusId,
                },
            })
            return data;
        }
        catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const saveForecastDemandProductsDraftedData = createAsyncThunk(
    'demandPlanning/saveForecastDemandProductsDraftedData',
    async (
        { demandPlanningId, demandPlanningYear, forecastDemandProductIds, draftedData },
        { rejectWithValue }
    ) => {
        try {
            const payload = {
                demandPlanningId,
                demandPlanningYear,
                forecastDemandProductIds,
                draftedData,
            }
            const { data } = await axios.put('/api/v1/forecast-demand-products-drafted-data', payload)
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const saveForecastDemandProductApproval = createAsyncThunk(
    'demandPlanning/saveForecastDemandProductApproval',
    async (
        { demandPlanningId, demandPlanningYear, productId, approved },
        { rejectWithValue }
    ) => {
        try {
            const payload = {
                demandPlanningId,
                demandPlanningYear,
                productId,
                approved,
            }
            const { data } = await axios.put('/api/v1/forecast-demand-product-approval', payload)
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const saveForecastDemandProductApprovedNotes = createAsyncThunk(
    'demandPlanning/saveForecastDemandProductApprovedNotes',
    async (
        { demandPlanningId, demandPlanningYear, productId, approvedNotes },
        { rejectWithValue }
    ) => {
        try {
            const payload = {
                demandPlanningId,
                demandPlanningYear,
                productId,
                approvedNotes,
            }
            const { data } = await axios.put('/api/v1/forecast-demand-product-approved-notes', payload)
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const saveForecastDemandProductUpcomingBuyExclude = createAsyncThunk(
    'demandPlanning/saveForecastDemandProductUpcomingBuyExclude',
    async (
        {
            demandPlanningId,
            demandPlanningYear,
            buyMonth,
            forecastDemandProductIds,
            excluded,
        },
        { rejectWithValue }
    ) => {
        try {
            const payload = {
                demandPlanningId,
                demandPlanningYear,
                buyMonth,
                forecastDemandProductIds,
                excluded,
            }
            const { data } = await axios.put(
                '/api/v1/forecast-demand-product-upcoming-buy-exclude',
                payload
            )
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const fetchForecastDemandPlanningList = createAsyncThunk(
    'demandPlanning/fetchForecastDemandPlanningList',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-demand-planning', {
                signal: new AbortController().signal,
            })
            return data
        } catch (error) {
            if (error.code === "ERR_CANCELED" || error.message === "canceled") {
                throw new Error("AbortError");
            }
            return rejectWithValue(extractError(error))
        }
    }
)

export const createForecastDemandPlanning = createAsyncThunk(
    'demandPlanning/createForecastDemandPlanning',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/forecast-demand-planning', payload)
            return data
        } catch (error) {
            const msg = extractError(error)
            return rejectWithValue(msg)
        }
    }
)

export const updateForecastDemandPlanning = createAsyncThunk(
    'demandPlanning/updateForecastDemandPlanning',
    async ({ id, silent, ...payload }, { rejectWithValue }) => {
        try {
            const { data } = await axios.put(`/api/v1/forecast-demand-planning/${id}`, payload)
            return { ...data, silent: !!silent }
        } catch (error) {
            const msg = extractError(error)
            return rejectWithValue(msg)
        }
    }
)

export const updateForecastDemandPlanRevenueTarget = createAsyncThunk(
    'demandPlanning/updateForecastDemandPlanRevenueTarget',
    async ({ id, revenueTarget, expectedUpdatedAt }, { rejectWithValue }) => {
        try {
            const { data } = await axios.post(
                `/api/v1/forecast-demand-planning/${id}/revenue-target`,
                { revenueTarget, expectedUpdatedAt }
            )
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getAiRebalanceHistory = createAsyncThunk(
    'demandPlanning/getAiRebalanceHistory',
    async ({ demandPlanningId, forecastYear, status }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/ai-rebalance-history', {
                params: {
                    demandPlanningId,
                    forecastYear,
                    status,
                },
            })
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const createAiRebalanceHistory = createAsyncThunk(
    'demandPlanning/createAiRebalanceHistory',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post('/api/v1/ai-rebalance-history', payload)
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const getForecastDemandPlanningHistory = createAsyncThunk(
    'demandPlanning/getForecastDemandPlanningHistory',
    async ({ demandPlanningId, forecastYear, search, userId, page, pageSize }, { rejectWithValue }) => {
        try {
            const { data } = await axios.get('/api/v1/forecast-demand-planning-history', {
                params: {
                    demandPlanningId,
                    forecastYear,
                    search,
                    userId,
                    page,
                    pageSize,
                },
            })
            return data
        } catch (error) {
            return rejectWithValue(extractError(error))
        }
    }
)

export const deleteForecastDemandPlanning = createAsyncThunk(
    'demandPlanning/deleteForecastDemandPlanning',
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await axios.delete(`/api/v1/forecast-demand-planning/${id}`)
            return data
        } catch (error) {
            const msg = extractError(error)
            return rejectWithValue(msg)
        }
    }
)

export const demandPlanningSlice = createSlice({
    name: 'demandPlanning',
    initialState,
    reducers: {
        setForecastDemandPlanningData: (state, action) => {
            state.forecastDemandPlanningData = action.payload;
            state.error = null;
        },
        clearForecastDemandPlanningHistory: (state) => {
            state.forecastDemandPlanningHistory = [];
            state.forecastDemandPlanningHistoryTotal = 0;
            state.forecastDemandPlanningHistoryPage = 1;
        },
        clearDemandPlanningTableData: (state) => {
            state.skuAllocationTableData = [];
            state.eligibilityValidationTableData = [];
            state.monthlyTrendTableData = [];
            state.historicalTableData = [];
            state.forecastVsActualsTableData = [];
            state.approvedBuysTableData = [];
            state.forecastBySkuTableData = [];
            state.openToBuyTableData = [];
            state.overstockInventoryTableData = [];
            state.upcomingBuysTableData = null;
            state.cashLiabilitiesTableData = [];
            state.demandDistributionProfileData = null;
            state.forecastDemandPlanningData = null;
            state.aiRebalanceHistory = [];
            state.forecastDemandPlanningHistory = [];
            state.forecastDemandPlanningHistoryTotal = 0;
            state.forecastDemandPlanningHistoryPage = 1;
            state.firstLoadDefaultTableComplete = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(getSkuAllocationTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getSkuAllocationTableData];
        })
        .addCase(getSkuAllocationTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.skuAllocationTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getSkuAllocationTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getSkuAllocationTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.skuAllocationTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getSkuAllocationTableData, state.loadingKeys);
        })
        .addCase(getForecastVsActualsTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getForecastVsActualsTableData];
        })
        .addCase(getForecastVsActualsTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.forecastVsActualsTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getForecastVsActualsTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getForecastVsActualsTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.forecastVsActualsTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getForecastVsActualsTableData, state.loadingKeys);
        })
        .addCase(getHistoricalTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getHistoricalTableData];
        })
        .addCase(getHistoricalTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.historicalTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getHistoricalTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getHistoricalTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.historicalTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getHistoricalTableData, state.loadingKeys);
        })
        .addCase(getEligibilityValidationTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getEligibilityValidationTableData];
        })
        .addCase(getEligibilityValidationTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.eligibilityValidationTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getEligibilityValidationTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getEligibilityValidationTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.eligibilityValidationTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getEligibilityValidationTableData, state.loadingKeys);
        })
        .addCase(getMonthlyTrendTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getMonthlyTrendTableData];
        })
        .addCase(getMonthlyTrendTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.monthlyTrendTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getMonthlyTrendTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getMonthlyTrendTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.monthlyTrendTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getMonthlyTrendTableData, state.loadingKeys);
        })
        .addCase(getUpcomingBuysTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getUpcomingBuysTableData];
        })
        .addCase(getUpcomingBuysTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.upcomingBuysTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getUpcomingBuysTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getUpcomingBuysTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.upcomingBuysTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getUpcomingBuysTableData, state.loadingKeys);
        })
        .addCase(getPoScheduleTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getPoScheduleTableData];
        })
        .addCase(getPoScheduleTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.poScheduleTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getPoScheduleTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getPoScheduleTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.poScheduleTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getPoScheduleTableData, state.loadingKeys);
        })
        .addCase(getBundleDemandExplosionTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getBundleDemandExplosionTableData];
        })
        .addCase(getBundleDemandExplosionTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.bundleDemandExplosionTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getBundleDemandExplosionTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getBundleDemandExplosionTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.bundleDemandExplosionTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getBundleDemandExplosionTableData, state.loadingKeys);
        })
        .addCase(getBundleReplacementShiftTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getBundleReplacementShiftTableData];
        })
        .addCase(getBundleReplacementShiftTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.bundleReplacementShiftTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getBundleReplacementShiftTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getBundleReplacementShiftTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.bundleReplacementShiftTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getBundleReplacementShiftTableData, state.loadingKeys);
        })
        .addCase(getReplacementDemandShiftTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getReplacementDemandShiftTableData];
        })
        .addCase(getReplacementDemandShiftTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.replacementDemandShiftTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getReplacementDemandShiftTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getReplacementDemandShiftTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.replacementDemandShiftTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getReplacementDemandShiftTableData, state.loadingKeys);
        })
        .addCase(getComponentDemandLedgerTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getComponentDemandLedgerTableData];
        })
        .addCase(getComponentDemandLedgerTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.componentDemandLedgerTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getComponentDemandLedgerTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getComponentDemandLedgerTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.componentDemandLedgerTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getComponentDemandLedgerTableData, state.loadingKeys);
        })
        .addCase(getPurchaseRoundingTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getPurchaseRoundingTableData];
        })
        .addCase(getPurchaseRoundingTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.purchaseRoundingTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getPurchaseRoundingTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getPurchaseRoundingTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.purchaseRoundingTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getPurchaseRoundingTableData, state.loadingKeys);
        })
        .addCase(getContainerOptimizationTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getContainerOptimizationTableData];
        })
        .addCase(getContainerOptimizationTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.containerOptimizationTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getContainerOptimizationTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getContainerOptimizationTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.containerOptimizationTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getContainerOptimizationTableData, state.loadingKeys);
        })
        .addCase(getInventoryCapitalPlanTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getInventoryCapitalPlanTableData];
        })
        .addCase(getInventoryCapitalPlanTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.inventoryCapitalPlanTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getInventoryCapitalPlanTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getInventoryCapitalPlanTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.inventoryCapitalPlanTableData = null;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getInventoryCapitalPlanTableData, state.loadingKeys);
        })
        .addCase(getCashLiabilitiesTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getCashLiabilitiesTableData];
        })
        .addCase(getCashLiabilitiesTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.cashLiabilitiesTableData = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getCashLiabilitiesTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getCashLiabilitiesTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.cashLiabilitiesTableData = [];
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getCashLiabilitiesTableData, state.loadingKeys);
        })
        .addCase(getOverstockInventoryTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getOverstockInventoryTableData];
        })
        .addCase(getOverstockInventoryTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.overstockInventoryTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getOverstockInventoryTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getOverstockInventoryTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.overstockInventoryTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getOverstockInventoryTableData, state.loadingKeys);
        })
        .addCase(getApprovedBuysTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getApprovedBuysTableData];
        })
        .addCase(getApprovedBuysTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.approvedBuysTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getApprovedBuysTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getApprovedBuysTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.approvedBuysTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getApprovedBuysTableData, state.loadingKeys);
        })
        .addCase(getForecastBySkuTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getForecastBySkuTableData];
        })
        .addCase(getForecastBySkuTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.forecastBySkuTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getForecastBySkuTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getForecastBySkuTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.forecastBySkuTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getForecastBySkuTableData, state.loadingKeys);
        })
        .addCase(getOpenToBuyTableData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getOpenToBuyTableData];
        })
        .addCase(getOpenToBuyTableData.fulfilled, (state, action) => {
            state.loading = false;
            state.openToBuyTableData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getOpenToBuyTableData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getOpenToBuyTableData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.openToBuyTableData = [];
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getOpenToBuyTableData, state.loadingKeys);
        })
        .addCase(saveForecastDemandProductOverride.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.saveForecastDemandProductOverride];
        })
        .addCase(saveForecastDemandProductOverride.fulfilled, (state, action) => {
            state.loading = false;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductOverride, state.loadingKeys);
            state.error = null;
        })
        .addCase(saveForecastDemandProductOverride.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductOverride, state.loadingKeys);
        })
        .addCase(resetForecastDemandProductOverrides.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.resetForecastDemandProductOverrides];
        })
        .addCase(resetForecastDemandProductOverrides.fulfilled, (state) => {
            state.loading = false;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.resetForecastDemandProductOverrides, state.loadingKeys);
            state.error = null;
        })
        .addCase(resetForecastDemandProductOverrides.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.resetForecastDemandProductOverrides, state.loadingKeys);
        })
        .addCase(getSkuAllocationChangeLog.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getSkuAllocationChangeLog];
        })
        .addCase(getSkuAllocationChangeLog.fulfilled, (state, action) => {
            state.skuAllocationChangeLog = action.payload?.items || [];
            state.skuAllocationChangeLogPagination = {
                currentPage: action.payload?.pagination?.currentPage || 1,
                totalPages: action.payload?.pagination?.totalPages || 1,
                totalCount: action.payload?.pagination?.totalCount || 0,
                limit: action.payload?.pagination?.limit || 100,
                isTheLastPage: Boolean(action.payload?.pagination?.isTheLastPage),
            };
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getSkuAllocationChangeLog, state.loadingKeys);
            state.error = null;
        })
        .addCase(getSkuAllocationChangeLog.rejected, (state, action) => {
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getSkuAllocationChangeLog, state.loadingKeys);
        })
        .addCase(rollbackSkuAllocationChanges.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.rollbackSkuAllocationChanges];
        })
        .addCase(rollbackSkuAllocationChanges.fulfilled, (state) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.rollbackSkuAllocationChanges, state.loadingKeys);
            state.error = null;
        })
        .addCase(rollbackSkuAllocationChanges.rejected, (state, action) => {
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.rollbackSkuAllocationChanges, state.loadingKeys);
        })
        .addCase(undoLastSkuAllocationBatch.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.undoLastSkuAllocationBatch];
        })
        .addCase(undoLastSkuAllocationBatch.fulfilled, (state) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.undoLastSkuAllocationBatch, state.loadingKeys);
            state.error = null;
        })
        .addCase(undoLastSkuAllocationBatch.rejected, (state, action) => {
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.undoLastSkuAllocationBatch, state.loadingKeys);
        })
        .addCase(restoreSkuAllocationToTimestamp.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.restoreSkuAllocationToTimestamp];
        })
        .addCase(restoreSkuAllocationToTimestamp.fulfilled, (state) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.restoreSkuAllocationToTimestamp, state.loadingKeys);
            state.error = null;
        })
        .addCase(restoreSkuAllocationToTimestamp.rejected, (state, action) => {
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.restoreSkuAllocationToTimestamp, state.loadingKeys);
        })
        .addCase(saveForecastDemandProductApproval.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.saveForecastDemandProductApproval];
        })
        .addCase(saveForecastDemandProductApproval.fulfilled, (state) => {
            state.loading = false;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductApproval, state.loadingKeys);
            state.error = null;
        })
        .addCase(saveForecastDemandProductApproval.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductApproval, state.loadingKeys);
        })
        .addCase(saveForecastDemandProductApprovedNotes.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.saveForecastDemandProductApprovedNotes];
        })
        .addCase(saveForecastDemandProductApprovedNotes.fulfilled, (state) => {
            state.loading = false;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductApprovedNotes, state.loadingKeys);
            state.error = null;
        })
        .addCase(saveForecastDemandProductApprovedNotes.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductApprovedNotes, state.loadingKeys);
        })
        .addCase(saveForecastDemandProductUpcomingBuyExclude.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [
                ...state.loadingKeys,
                DemandPlanningLoadingKeys.saveForecastDemandProductUpcomingBuyExclude,
            ];
        })
        .addCase(saveForecastDemandProductUpcomingBuyExclude.fulfilled, (state) => {
            state.loading = false;
            state.loadingKeys = removeLoadingKey(
                DemandPlanningLoadingKeys.saveForecastDemandProductUpcomingBuyExclude,
                state.loadingKeys
            );
            state.error = null;
        })
        .addCase(saveForecastDemandProductUpcomingBuyExclude.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(
                DemandPlanningLoadingKeys.saveForecastDemandProductUpcomingBuyExclude,
                state.loadingKeys
            );
        })
        .addCase(saveForecastDemandProductsDraftedData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.saveForecastDemandProductsDraftedData];
        })
        .addCase(saveForecastDemandProductsDraftedData.fulfilled, (state) => {
            state.loading = false;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductsDraftedData, state.loadingKeys);
            state.error = null;
        })
        .addCase(saveForecastDemandProductsDraftedData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.saveForecastDemandProductsDraftedData, state.loadingKeys);
        })
        .addCase(getDemandDistributionProfileData.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getDemandDistributionProfileData];
        })
        .addCase(getDemandDistributionProfileData.fulfilled, (state, action) => {
            state.loading = false;
            state.demandDistributionProfileData = action.payload;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getDemandDistributionProfileData, state.loadingKeys);
            state.error = null;
        })
        .addCase(getDemandDistributionProfileData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.demandDistributionProfileData = null;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getDemandDistributionProfileData, state.loadingKeys);
        })
        .addCase(getForecastDemandPlanningData.pending, (state) => {
            state.forecastCatalogGetError = null;
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getForecastDemandPlanningData];
        })
        .addCase(getForecastDemandPlanningData.fulfilled, (state, action) => {
            state.forecastDemandPlanningData = action.payload;
            state.forecastCatalogGetError = null;
            state.firstLoadDefaultTableComplete = true;
            state.loadingKeys = removeOneLoadingKey(
                DemandPlanningLoadingKeys.getForecastDemandPlanningData,
                state.loadingKeys
            );
        })
        .addCase(getForecastDemandPlanningData.rejected, (state, action) => {
            const isAbort =
                action.payload?.__abort === true ||
                action.error?.message === 'AbortError'
            state.loadingKeys = removeOneLoadingKey(
                DemandPlanningLoadingKeys.getForecastDemandPlanningData,
                state.loadingKeys
            );
            if (isAbort) {
                return;
            }
            state.firstLoadDefaultTableComplete = true;
            state.forecastCatalogGetError = action.payload;
            // Keep existing catalog on failed refresh — do not wipe LTM metrics hydrated from saved `data[year].api`.
        })
        .addCase(refreshForecastDemandSalesSnapshot.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.refreshForecastDemandSalesSnapshot];
        })
        .addCase(refreshForecastDemandSalesSnapshot.fulfilled, (state, action) => {
            const { item, api } = action.payload || {};
            if (api) {
                state.forecastDemandPlanningData = api;
            }
            if (item?.id != null && Array.isArray(state.forecastDemandPlanningList)) {
                state.forecastDemandPlanningList = state.forecastDemandPlanningList.map((row) =>
                    row.id === item.id ? { ...row, ...item } : row
                );
            }
            state.loadingKeys = removeLoadingKey(
                DemandPlanningLoadingKeys.refreshForecastDemandSalesSnapshot,
                state.loadingKeys
            );
        })
        .addCase(refreshForecastDemandSalesSnapshot.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(
                DemandPlanningLoadingKeys.refreshForecastDemandSalesSnapshot,
                state.loadingKeys
            );
            state.error = action.payload;
        })
        .addCase(fetchForecastDemandPlanningList.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.fetchForecastDemandPlanningList];
            state.forecastDemandPlanningListFetchFailed = false;
        })
        .addCase(fetchForecastDemandPlanningList.fulfilled, (state, action) => {
            state.forecastDemandPlanningList = action.payload.items || [];
            state.forecastDemandPlanningListLoaded = true;
            state.forecastDemandPlanningListFetchFailed = false;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.fetchForecastDemandPlanningList, state.loadingKeys);
            state.error = null;
        })
        .addCase(fetchForecastDemandPlanningList.rejected, (state, action) => {
            state.forecastDemandPlanningList = [];
            state.forecastDemandPlanningListFetchFailed = true;
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.fetchForecastDemandPlanningList, state.loadingKeys);
            state.error = action.payload;
        })
        .addCase(createForecastDemandPlanning.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.createForecastDemandPlanning];
        })
        .addCase(createForecastDemandPlanning.fulfilled, (state, action) => {
            const item = action.payload?.item;
            if (item) {
                state.forecastDemandPlanningList = [item, ...state.forecastDemandPlanningList];
            }
            toast.success('Demand planning created.', { position: 'bottom-right' });
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.createForecastDemandPlanning, state.loadingKeys);
            state.error = null;
        })
        .addCase(createForecastDemandPlanning.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.createForecastDemandPlanning, state.loadingKeys);
            toast.error(
                typeof action.payload === 'string' ? action.payload : 'Could not save demand planning.',
                { position: 'bottom-right' }
            )
            state.error = action.payload;
        })
        .addCase(updateForecastDemandPlanning.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.updateForecastDemandPlanning];
        })
        .addCase(updateForecastDemandPlanning.fulfilled, (state, action) => {
            const payload = action.payload;
            const item = payload?.item;
            if (item) {
                state.forecastDemandPlanningList = state.forecastDemandPlanningList.map((r) =>
                    r.id === item.id ? item : r
                );
            }
            if (!payload?.silent) {
                toast.success('Demand planning updated.', { position: 'bottom-right' });
            }
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.updateForecastDemandPlanning, state.loadingKeys);
            state.error = null;
        })
        .addCase(updateForecastDemandPlanning.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.updateForecastDemandPlanning, state.loadingKeys);
            toast.error(
                typeof action.payload === 'string' ? action.payload : 'Could not update demand planning.',
                { position: 'bottom-right' }
            )
            state.error = action.payload;
        })
        .addCase(getAiRebalanceHistory.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getAiRebalanceHistory];
        })
        .addCase(getAiRebalanceHistory.fulfilled, (state, action) => {
            state.aiRebalanceHistory = Array.isArray(action.payload?.items) ? action.payload.items : [];
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getAiRebalanceHistory, state.loadingKeys);
            state.error = null;
        })
        .addCase(getAiRebalanceHistory.rejected, (state, action) => {
            state.aiRebalanceHistory = [];
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getAiRebalanceHistory, state.loadingKeys);
            state.error = action.payload;
        })
        .addCase(createAiRebalanceHistory.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.createAiRebalanceHistory];
        })
        .addCase(createAiRebalanceHistory.fulfilled, (state, action) => {
            const item = action.payload?.item;
            if (item) {
                state.aiRebalanceHistory = [item, ...state.aiRebalanceHistory.filter((row) => row.id !== item.id)];
            }
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.createAiRebalanceHistory, state.loadingKeys);
            state.error = null;
        })
        .addCase(createAiRebalanceHistory.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.createAiRebalanceHistory, state.loadingKeys);
            toast.error(
                typeof action.payload === 'string' ? action.payload : 'Could not save AI rebalance decision.',
                { position: 'bottom-right' }
            )
            state.error = action.payload;
        })
        .addCase(deleteForecastDemandPlanning.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.deleteForecastDemandPlanning];
        })
        .addCase(deleteForecastDemandPlanning.fulfilled, (state, action) => {
            const id = action.payload?.id;
            if (id != null) {
                state.forecastDemandPlanningList = state.forecastDemandPlanningList.filter((r) => r.id !== id);
            }
            toast.success('Demand planning deleted.', { position: 'bottom-right' });
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.deleteForecastDemandPlanning, state.loadingKeys);
            state.error = null;
        })
        .addCase(deleteForecastDemandPlanning.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.deleteForecastDemandPlanning, state.loadingKeys);
            toast.error(
                typeof action.payload === 'string' ? action.payload : 'Could not delete demand planning.',
                { position: 'bottom-right' }
            )
            state.error = action.payload;
        })
        .addCase(getForecastDemandPlanningHistory.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, DemandPlanningLoadingKeys.getForecastDemandPlanningHistory];
        })
        .addCase(getForecastDemandPlanningHistory.fulfilled, (state, action) => {
            const requestedPage = Number(action.meta?.arg?.page || 1);
            const items = action.payload?.items || [];
            state.forecastDemandPlanningHistory = requestedPage > 1
                ? [...state.forecastDemandPlanningHistory, ...items]
                : items;
            state.forecastDemandPlanningHistoryTotal = Number(action.payload?.total || 0);
            state.forecastDemandPlanningHistoryPage = Number(action.payload?.page || requestedPage);
            state.forecastDemandPlanningHistoryPageSize = Number(
                action.payload?.pageSize || action.meta?.arg?.pageSize || 25
            );
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getForecastDemandPlanningHistory, state.loadingKeys);
            state.error = null;
        })
        .addCase(getForecastDemandPlanningHistory.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(DemandPlanningLoadingKeys.getForecastDemandPlanningHistory, state.loadingKeys);
            state.error = action.payload;
        })
    }
})

export const { setForecastDemandPlanningData, clearDemandPlanningTableData, clearForecastDemandPlanningHistory } = demandPlanningSlice.actions;
export default demandPlanningSlice.reducer;
